import { clsx } from 'clsx';
import { Icon, IconType } from 'atoms/Icon';
import { Image } from 'atoms/Image/Image';
import { Link } from 'atoms/Link/Link';
import { Logo } from 'molecules/Logo/Logo';
import { WishlistIcon } from 'organisms/Header/WishlistIcon';
import { useGTMCurrentBasket } from 'modules/gtm/modules/sections/cart/hooks';
import {
  gtmPushBurgerMenuMobile,
  gtmPushShopNavigation,
} from 'modules/gtm/modules/sections/navigation/navigation';
import { pushOutboundClick } from 'modules/gtm/modules/sections/outboudEvents/badges';
import { useGTMCurrentWishlist } from 'modules/gtm/modules/sections/wishlist/hooks';
import { useTranslations } from 'modules/i18n/useTranslations';
import { useMediaQuery } from 'modules/mui/hooks';
import { usePopoverManager } from 'modules/shop/popoverManager/hooks';
import { PopoverType } from 'modules/shop/popoverManager/types';
import { AppRoutes } from 'modules/shop/routes';
import { CartIcon } from '../CartIcon';
import computerBildTopShop from './images/computerBildTopShop.svg';
import deutscherOnlineHandelAward from './images/deutscherOnlineHandelAward.svg';
import gueteZeichen from './images/guetezeichen.svg';
import shopAuskunft from './images/shopAuskunft.svg';
import signImg from './images/sign.svg';
import trustEcommerceEurope from './images/trustEcommerceEurope.svg';
import trustedShop from './images/trustedShop.svg';
import trustMarkHandelsVerband from './images/trustmarkHandelsverband.svg';
import styles from './HeaderLine.module.css';

export const HeaderLine = () => {
  const t = useTranslations();
  const { isMobile } = useMediaQuery();
  const { openSidebarPopover, openPopover } = usePopoverManager();
  const gtmEisLogoEventLabel = t('header.gtmNavigationLabels.HomeLogo');
  const gtmSearchEventLabel = t('header.gtmNavigationLabels.searchIcon');

  useGTMCurrentWishlist();
  useGTMCurrentBasket();

  const handleBadgeClick = (href: string, isExternal: boolean) => {
    if (isExternal) {
      pushOutboundClick(href);
    }
  };

  const handleBurgerClick = () => {
    gtmPushBurgerMenuMobile();
    openSidebarPopover();
  };

  const handleEisLogoClick = () => {
    gtmPushShopNavigation(gtmEisLogoEventLabel);
  };

  const handleMobileSearchIconClick = () => {
    gtmPushShopNavigation(gtmSearchEventLabel);
  };

  const badges = [
    {
      href: 'https://www.ecommercetrustmark.eu/the-code-of-conduct/',
      src: trustEcommerceEurope.src,
      alt: 'trust Ecommerce Europe',
      isExternal: true,
    },
    {
      href: 'https://www.guetezeichen.at/zertifikate/shopzertifikat/eisat/',
      src: gueteZeichen.src,
      alt: 'Österreichisches E-Commerce-Gütezeichen',
      isExternal: true,
      additionalClass: 'doubleIcon',
    },
    {
      href: 'https://www.handelsverband.at/fileadmin/content/Trustmark/Zertifikate/TrustmarkZertifikat_eis.at.pdf',
      src: trustMarkHandelsVerband.src,
      alt: 'Trustmark HandelsVerband',
      isExternal: true,
    },
    {
      href: t('header.certificate-doha-link'),
      src: deutscherOnlineHandelAward.src,
      alt: 'Deutscher Online Handel Award',
      isExternal: false,
    },
    {
      href: '#',
      src: trustedShop.src,
      alt: 'Trusted Shop',
      isExternal: false,
    },
    {
      href: 'http://www.shopauskunft.de/bewertung/EISat--S-23358.html',
      src: shopAuskunft.src,
      alt: 'Shopauskunft',
      isExternal: true,
    },
    {
      href: t('header.certificate-cp-link'),
      src: computerBildTopShop.src,
      alt: 'Computer Bild Top Shop',
      isExternal: false,
    },
  ];

  const trustedShopBadge = badges.find((badge) => badge.alt === 'Trusted Shop');

  return (
    <div className={styles.root}>
      <Icon
        type={IconType.burger}
        className={styles.burger}
        onClick={handleBurgerClick}
      />
      <Link href="/" className={styles.logo} onClick={handleEisLogoClick}>
        <Logo />
      </Link>
      <Image src={signImg.src} className={styles.sign} alt={signImg.alt} />

      <div className={styles.badges}>
        {isMobile
          ? trustedShopBadge && (
              <a href={trustedShopBadge.href}>
                <Image
                  src={trustedShopBadge.src}
                  className={styles.badge}
                  alt={trustedShopBadge.alt}
                />
              </a>
            )
          : badges.map((badge, index) => (
              <a
                key={index}
                href={badge.href}
                target={badge.isExternal ? '_blank' : undefined}
                rel={badge.isExternal ? 'noopener noreferrer' : undefined}
                className={clsx(
                  !!badge.additionalClass && styles[badge.additionalClass],
                )}
                onClick={() => handleBadgeClick(badge.href, badge.isExternal)}
              >
                <Image
                  src={badge.src}
                  className={styles.badge}
                  alt={badge.alt}
                />
              </a>
            ))}
      </div>

      <div className={styles.icons}>
        <Link href={AppRoutes.SEARCH} onClick={handleMobileSearchIconClick}>
          <Icon className={styles.icon} type={IconType.search} />
        </Link>
        <WishlistIcon className={styles.icon} />
        <CartIcon
          className={styles.icon}
          onClick={() => {
            openPopover(PopoverType.CART);
          }}
        />
      </div>
    </div>
  );
};
